import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { logger } from "@app/services/logger";
import * as xgbApi from "@app/services/xgboostAdminApi";
import type {
  XGBoostOverview,
  DecisionEvent,
  DecisionDetail,
  MemorySummary,
  SampleItem,
  TrainingRun,
  ModelRegistryItem,
  BackfillJob,
  BackfillProgress,
  DriftSnapshot,
  PerformanceResponse,
  PerformanceItem,
} from "@app/services/xgboostAdminApi";

type SubTab = "overview" | "activity" | "memory" | "training" | "models" | "drift" | "backfill" | "performance";

const SUB_TABS: Array<{ id: SubTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "activity", label: "Live Activity" },
  { id: "memory", label: "Memory" },
  { id: "training", label: "Training" },
  { id: "models", label: "Models" },
  { id: "drift", label: "Drift & Health" },
  { id: "backfill", label: "Backfill" },
  { id: "performance", label: "Performance" },
];

function getScoreColor(score: number | null): string {
  if (score === null || score < 0) return "#6b7280";
  if (score >= 0.8) return "#22c55e";
  if (score >= 0.6) return "#eab308";
  return "#ef4444";
}

function getPassBadge(passed: boolean | null): { label: string; color: string } {
  if (passed === null) return { label: "SKIP", color: "#6b7280" };
  if (passed) return { label: "PASS", color: "#22c55e" };
  return { label: "REJECT", color: "#ef4444" };
}

function getStatusColor(status: string): string {
  switch (status) {
    case "completed": return "#22c55e";
    case "running":
    case "pending": return "#eab308";
    case "failed": return "#ef4444";
    default: return "#6b7280";
  }
}

function getAlertColor(level: string | null): string {
  switch (level) {
    case "critical": return "#ef4444";
    case "warning": return "#eab308";
    default: return "#22c55e";
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "24px", maxWidth: "1400px", margin: "0 auto" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  headerRight: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  cardsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" },
  statCard: { padding: "16px", borderRadius: "8px", background: "rgba(30, 41, 59, 0.8)", border: "1px solid rgba(148, 163, 184, 0.15)" },
  statValue: { fontSize: "1.5rem", fontWeight: 700, marginTop: "8px" },
  statLabel: { fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.7)", fontWeight: 500 },
  subtitle: { fontSize: "0.85rem", color: "rgba(203, 213, 225, 0.7)", marginTop: "4px" },
  subTabsNav: { display: "flex", gap: "4px", marginBottom: "24px", borderBottom: "1px solid rgba(148, 163, 184, 0.15)", overflowX: "auto" as const },
  subTabButton: { padding: "10px 20px", border: "none", background: "transparent", color: "rgba(203, 213, 225, 0.7)", cursor: "pointer", fontSize: "0.9rem", fontWeight: 500, borderBottom: "2px solid transparent", transition: "all 0.2s ease", whiteSpace: "nowrap" as const },
  sectionTitle: { fontSize: "1.1rem", fontWeight: 600, marginBottom: "16px", color: "#f1f5f9" },
  table: { width: "100%", borderCollapse: "collapse" as const, fontSize: "0.85rem" },
  th: { padding: "10px 12px", textAlign: "left" as const, borderBottom: "1px solid rgba(148, 163, 184, 0.15)", color: "rgba(203, 213, 225, 0.7)", fontWeight: 600, fontSize: "0.8rem", textTransform: "uppercase" as const, letterSpacing: "0.3px", whiteSpace: "nowrap" as const },
  td: { padding: "10px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.08)", verticalAlign: "middle" as const, fontSize: "0.85rem" },
  emptyState: { padding: "40px 20px", textAlign: "center" as const, color: "rgba(203, 213, 225, 0.5)" },
  errorState: { padding: "24px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", textAlign: "center" as const, marginBottom: "16px" },
  actionButton: { padding: "8px 16px", borderRadius: "6px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.8rem" },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: "4px", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" as const },
};

function EmptyState({ message }: { message: string }) {
  return <div style={styles.emptyState}><div style={{ fontSize: "0.9rem" }}>{message}</div></div>;
}

function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={styles.errorState}>
      <div style={{ fontSize: "0.85rem", color: "#fca5a5", marginBottom: "12px" }}>{message}</div>
      {onRetry && <Button type="button" variant="ghost" size="small" onClick={onRetry}>Retry</Button>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span style={{ ...styles.badge, color: getStatusColor(status), background: `${getStatusColor(status)}22` }}>
      {status}
    </span>
  );
}

function PassBadge({ passed }: { passed: boolean | null }) {
  const info = getPassBadge(passed);
  return <span style={{ ...styles.badge, color: info.color, background: `${info.color}22` }}>{info.label}</span>;
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return "-";
  try { return new Date(val).toLocaleString("en-US"); } catch { return "-"; }
}

function fmtNum(val: number | null | undefined, decimals = 2): string {
  if (val == null) return "-";
  return val.toFixed(decimals);
}

function formatLocalDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRelativeLocalDateValue(daysAgo: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysAgo);
  return formatLocalDateValue(date);
}

function HelpTip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: "6px", cursor: "pointer" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} onClick={() => setShow(!show)}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: "16px", height: "16px", borderRadius: "50%",
        background: "rgba(148,163,184,0.2)", color: "rgba(148,163,184,0.8)",
        fontSize: "10px", fontWeight: 700, lineHeight: "16px", userSelect: "none",
      }}>?</span>
      {show && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
          padding: "8px 12px", borderRadius: "6px", background: "#1e293b",
          border: "1px solid rgba(148,163,184,0.2)", boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          color: "rgba(203,213,225,0.9)", fontSize: "0.75rem", lineHeight: 1.4,
          minWidth: "220px", maxWidth: "320px", zIndex: 100, whiteSpace: "normal" as const,
          textAlign: "left" as const, fontWeight: 400,
        }}>{text}</div>
      )}
    </span>
  );
}

function InfoBanner({ title, paragraphs }: { title: string; paragraphs: string[] }) {
  return (
    <div style={{
      padding: "12px 16px", borderRadius: "8px", marginBottom: "20px",
      background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <span style={{ fontSize: "1rem" }}>💡</span>
        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#93c5fd" }}>{title}</span>
      </div>
      {paragraphs.map((p, i) => (
        <p key={i} style={{ margin: "4px 0 0", fontSize: "0.8rem", color: "rgba(203,213,225,0.75)", lineHeight: 1.5 }}>{p}</p>
      ))}
    </div>
  );
}

export function XGBoostControlCenterTab(): JSX.Element {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("overview");
  const [overview, setOverview] = useState<XGBoostOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await xgbApi.getOverview();
      setOverview(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load overview";
      setOverviewError(msg);
      logger.error("[XGBoostTab] loadOverview:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeSubTab === "overview") loadOverview();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeSubTab, loadOverview]);

  return (
    <div style={styles.container}>
      <style>{`
        .xgb-sort-th { cursor: pointer !important; }
        .xgb-sort-th:hover { background: rgba(59,130,246,0.1) !important; }
        .xgb-date-btn { cursor: pointer !important; }
      `}</style>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={{ margin: 0, color: "#f1f5f9" }}>XGBoost Control Center</h2>
        </div>
      </div>
      <div style={styles.subTabsNav}>
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              ...styles.subTabButton,
              ...(activeSubTab === tab.id ? { color: "#fff", borderBottom: "2px solid #3b82f6" } : {}),
            }}>{tab.label}</button>
        ))}
      </div>

      {activeSubTab === "overview" && <OverviewPanel overview={overview} loading={overviewLoading} error={overviewError} onRetry={loadOverview} />}
      {activeSubTab === "activity" && <LiveActivityPanel />}
      {activeSubTab === "memory" && <MemoryPanel />}
      {activeSubTab === "training" && <TrainingPanel />}
      {activeSubTab === "models" && <ModelsPanel />}
      {activeSubTab === "drift" && <DriftPanel />}
      {activeSubTab === "backfill" && <BackfillPanel />}
      {activeSubTab === "performance" && <PerformancePanel />}
    </div>
  );
}

/* ── Live Activity Panel ── */
function LiveActivityPanel() {
  const [decisions, setDecisions] = useState<DecisionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterPassed, setFilterPassed] = useState<string>("all");
  const [filterStage, setFilterStage] = useState<string>("all");
  const [searchSymbol, setSearchSymbol] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DecisionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortBy, setSortBy] = useState<string>("time");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [datePreset, setDatePreset] = useState<string>("today");

  const getDateRange = useCallback((): { dateFrom?: string; dateTo?: string } => {
    switch (datePreset) {
      case "today":
        return { dateFrom: getRelativeLocalDateValue(0), dateTo: getRelativeLocalDateValue(0) };
      case "yesterday":
        return { dateFrom: getRelativeLocalDateValue(1), dateTo: getRelativeLocalDateValue(1) };
      case "last3":
        return { dateFrom: getRelativeLocalDateValue(2), dateTo: getRelativeLocalDateValue(0) };
      case "last7":
        return { dateFrom: getRelativeLocalDateValue(6), dateTo: getRelativeLocalDateValue(0) };
      default:
        return {};
    }
  }, [datePreset]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = filterPassed === "all" ? undefined : filterPassed === "true";
      const s = filterStage === "all" ? undefined : filterStage;
      const dateRange = getDateRange();
      const data = await xgbApi.getDecisions(page, 50, {
        passed: p, stage: s, symbol: searchSymbol || undefined,
        sortBy, sortOrder, dateFrom: dateRange.dateFrom, dateTo: dateRange.dateTo,
      });
      setDecisions(data.decisions);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load decisions");
    } finally {
      setLoading(false);
    }
  }, [page, filterPassed, filterStage, searchSymbol, sortBy, sortOrder, getDateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 15 seconds (silent, no loading state)
  useEffect(() => {
    const interval = setInterval(() => {
      loadData();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleSort = useCallback((col: string) => {
    if (sortBy === col) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortOrder("desc");
    }
    setPage(1);
  }, [sortBy]);

  const COLUMNS = ["Time", "Symbol", "Stage", "Score", "Threshold", "Result", "Reason", "Model"];
  const COL_KEYS: Record<string, string> = {
    "Time": "time", "Symbol": "symbol", "Stage": "stage", "Score": "score",
    "Threshold": "threshold", "Result": "result", "Reason": "reason", "Model": "model",
  };

  const DATE_PRESETS: Array<{ key: string; label: string }> = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "last3", label: "Last 3 Days" },
    { key: "last7", label: "Last 7 Days" },
    { key: "all", label: "All" },
  ];

  const loadDetail = useCallback(async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const data = await xgbApi.getDecisionDetail(id);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  if (selectedId) {
    if (detailLoading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading detail...</div>;
    if (!detail) return <div><Button variant="ghost" size="small" onClick={() => setSelectedId(null)}>Back</Button><ErrorState message="Detail not found" /></div>;
    return (
      <div>
        <Button variant="ghost" size="small" onClick={() => setSelectedId(null)}>Back to list</Button>
        <h3 style={{ ...styles.sectionTitle, marginTop: "16px" }}>Decision: {detail.symbol}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "12px", marginTop: "12px" }}>
          <InfoRow label="Score" value={detail.score !== null && detail.score >= 0 ? detail.score.toFixed(4) : "N/A"} />
          <InfoRow label="Threshold" value={detail.threshold?.toFixed(2) ?? "N/A"} />
          <InfoRow label="Stage" value={detail.stage} />
          <InfoRow label="Direction" value={detail.direction ?? "N/A"} />
          <InfoRow label="Model Version" value={detail.modelVersion ?? "N/A"} />
          <InfoRow label="Role" value={detail.modelRole ?? "N/A"} />
          <InfoRow label="Mode" value={detail.modelMode ?? "N/A"} />
          <InfoRow label="Reason" value={detail.reason ?? "N/A"} />
          <InfoRow label="Passed" value={detail.passed !== null ? (detail.passed ? "Yes" : "No") : "N/A"} />
          <InfoRow label="Signals Available" value={detail.signalsAvailable !== null ? (detail.signalsAvailable ? "Yes" : "No") : "N/A"} />
          <InfoRow label="Time" value={detail.createdAt ? fmtDate(detail.createdAt) : "N/A"} />
          <InfoRow label="Recommendation" value={detail.recommendationId ?? "N/A"} />
          <InfoRow label="Trade Outcome" value={detail.tradeOutcomeId ?? "Not linked"} />
        </div>
        {detail.prefilterResults && (
          <div style={{ marginTop: "12px" }}>
            <h4 style={{ color: "#f1f5f9", marginBottom: "8px" }}>Pre-filter Results</h4>
            <pre style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", maxHeight: "200px", overflow: "auto", background: "rgba(15,23,42,0.6)", padding: "12px", borderRadius: "8px" }}>
              {JSON.stringify(detail.prefilterResults, null, 2)}
            </pre>
          </div>
        )}
        {detail.tradeOutcomeId && (
          <div style={{ marginTop: "12px", padding: "12px", borderRadius: "8px", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)" }}>
            <h4 style={{ color: "#22c55e", margin: "0 0 4px", fontSize: "0.9rem" }}>Linked Trade Outcome</h4>
            <span style={{ fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", fontFamily: "monospace" }}>{detail.tradeOutcomeId}</span>
          </div>
        )}
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / 50));

  return (
    <div>
      <InfoBanner
        title="How decisions are recorded"
        paragraphs={[
          "Each time XGBoost evaluates a symbol, a decision event is logged here with its score, threshold, pass/reject result, and the full feature snapshot (136 indicators).",
          "Existing historical decisions from before this system was installed are NOT automatically migrated. Use Backfill tab to generate labeled samples from historical OHLCV data.",
          "Click any row to see the full feature snapshot, pre-filter results, and linked trade outcome.",
        ]}
      />
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        {DATE_PRESETS.map(dp => (
          <button
            key={dp.key}
            type="button"
            onClick={() => { setDatePreset(dp.key); setPage(1); }}
            style={{
              padding: "6px 14px", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.8rem",
              border: datePreset === dp.key ? "1px solid #3b82f6" : "1px solid rgba(148,163,184,0.2)",
              background: datePreset === dp.key ? "rgba(59,130,246,0.15)" : "rgba(15,23,42,0.6)",
              color: datePreset === dp.key ? "#93c5fd" : "rgba(203,213,225,0.7)",
              transition: "all 0.15s ease",
            }}
          >{dp.label}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: "12px", marginBottom: "8px", flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterPassed} onChange={(e) => { setFilterPassed(e.target.value); setPage(1); }} style={{ padding: "6px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
          <option value="all">All Results</option>
          <option value="true">Passed</option>
          <option value="false">Rejected</option>
        </select><HelpTip text="Filter by pass/reject result. Pass means the score exceeded the threshold." />
        <select value={filterStage} onChange={(e) => { setFilterStage(e.target.value); setPage(1); }} style={{ padding: "6px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
          <option value="all">All Stages</option>
          <option value="pair_selection">Pair Selection</option>
          <option value="live_gate">Live Gate</option>
          <option value="shadow_eval">Shadow</option>
          <option value="backfill_replay">Backfill</option>
        </select><HelpTip text="Stage where the XGBoost filter was applied. Pair Selection runs continuously on the watchlist." />
        <input type="text" value={searchSymbol} onChange={(e) => { setSearchSymbol(e.target.value); setPage(1); }} placeholder="Search symbol..." style={{ padding: "6px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)", width: "180px", fontSize: "0.85rem" }} />
        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
          style={{ padding: "6px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)", fontSize: "0.8rem" }}
        >
          <option value="time">Sort: Time</option>
          <option value="symbol">Sort: Symbol</option>
          <option value="stage">Sort: Stage</option>
          <option value="score">Sort: Score</option>
          <option value="threshold">Sort: Threshold</option>
          <option value="result">Sort: Result</option>
          <option value="reason">Sort: Reason</option>
          <option value="model">Sort: Model</option>
        </select>
        <button
          type="button"
          onClick={() => { setSortOrder(prev => prev === "asc" ? "desc" : "asc"); setPage(1); }}
          style={{
            padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.8rem",
            border: "1px solid rgba(148,163,184,0.2)", background: "rgba(15,23,42,0.6)",
            color: "rgba(203,213,225,0.8)",
          }}
        >{sortOrder === "asc" ? "↑ ASC" : "↓ DESC"}</button>
        <button
          type="button"
          onClick={() => loadData()}
          disabled={loading}
          style={{
            padding: "6px 14px", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.8rem",
            border: "1px solid rgba(59,130,246,0.4)", background: "rgba(59,130,246,0.12)",
            color: "#93c5fd",
          }}
        >↻ Refresh</button>
        <span style={{ fontSize: "0.65rem", color: "rgba(203,213,225,0.3)", fontFamily: "monospace" }}>
          auto-refresh 15s
        </span>
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>Loading decisions...</div>
      ) : error ? (
        <ErrorState message={error} onRetry={loadData} />
      ) : decisions.length === 0 ? (
        <EmptyState message="No decision events recorded yet." />
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {COLUMNS.map((h) => {
                    const colKey = COL_KEYS[h];
                    const sorted = sortBy === colKey ? ` ${sortOrder === "asc" ? "▲" : "▼"}` : "";
                    return (
                      <th
                        key={h}
                        onClick={() => toggleSort(colKey)}
                        style={{
                          ...styles.th,
                          cursor: "pointer",
                          userSelect: "none",
                          background: sortBy === colKey ? "rgba(59,130,246,0.12)" : "transparent",
                          color: sortBy === colKey ? "#93c5fd" : "rgba(203,213,225,0.7)",
                        }}
                        className="xgb-sort-th"
                      >{h}{sorted}</th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {decisions.map((d) => (
                  <tr key={d.id} onClick={() => loadDetail(d.id)} style={{ cursor: "pointer" }}>
                    <td style={styles.td}><span style={{ color: "rgba(203,213,225,0.6)", fontSize: "0.8rem" }}>{fmtDate(d.createdAt)}</span></td>
                    <td style={{ ...styles.td, fontWeight: 600, color: "#f1f5f9" }}>{d.symbol}</td>
                    <td style={styles.td}><span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)" }}>{d.stage}</span></td>
                    <td style={styles.td}><span style={{ color: getScoreColor(d.score), fontWeight: 600 }}>{d.score !== null && d.score >= 0 ? d.score.toFixed(4) : "-"}</span></td>
                    <td style={styles.td}><span style={{ color: "rgba(203,213,225,0.6)" }}>{d.threshold?.toFixed(2) ?? "-"}</span></td>
                    <td style={styles.td}><PassBadge passed={d.passed} /></td>
                    <td style={{ ...styles.td, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.reason ?? "-"}</td>
                    <td style={{ ...styles.td, fontSize: "0.75rem", color: "rgba(203,213,225,0.6)" }}>{d.modelVersion ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>
            <span style={{ fontSize: "0.8rem", color: "rgba(203,213,225,0.6)" }}>Page {page} of {pageCount} ({total} total)</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="ghost" size="small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <Button variant="ghost" size="small" onClick={() => setPage((p) => p + 1)} disabled={page * 50 >= total}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Memory Panel ── */
function MemoryPanel() {
  const [summary, setSummary] = useState<MemorySummary | null>(null);
  const [samples, setSamples] = useState<SampleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [curating, setCurating] = useState(false);
  const [sampleFilter, setSampleFilter] = useState<string>("all");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, smp] = await Promise.all([
        xgbApi.getMemorySummary(),
        xgbApi.getSamples(sampleFilter !== "all" ? sampleFilter : undefined, undefined, 0, 50),
      ]);
      setSummary(s);
      setSamples(smp.samples);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load memory data");
    } finally {
      setLoading(false);
    }
  }, [sampleFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCurate = async () => {
    setCurating(true);
    try {
      await xgbApi.runCuration();
      await loadData();
    } catch {
      setError("Curation failed");
    } finally {
      setCurating(false);
    }
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading memory...</div>;
  if (error) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h3 style={styles.sectionTitle}>Evolutionary Memory</h3>
        <div style={{ color: "#f87171", background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
          <strong>Error:</strong> {error}
        </div>
        <Button variant="secondary" size="small" onClick={loadData}>Retry</Button>
      </div>
    );
  }
  return (
    <div>
      <h3 style={styles.sectionTitle}>Evolutionary Memory</h3>
      {summary && (
        <div style={styles.cardsRow}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Samples</div>
            <div style={styles.statValue}>{summary.totalSamples ?? 0}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Trainable</div>
            <div style={styles.statValue}>{summary.trainableSamples ?? 0}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Recent 7d</div>
            <div style={styles.statValue}>{summary.recent7d ?? 0}</div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <Button variant="primary" size="small" onClick={handleCurate} disabled={curating}>Curate</Button>
        <select value={sampleFilter} onChange={(e) => setSampleFilter(e.target.value)} style={{ padding: "6px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
          <option value="all">All</option>
          <option value="triple_barrier">Triple Barrier</option>
          <option value="triple_barrier_backfill">Triple Barrier Backfill</option>
          <option value="historical_backfill">Historical Backfill</option>
          <option value="historical_cache">Historical Cache</option>
        </select>
      </div>
      {samples.length === 0 ? <EmptyState message="No samples" /> : (
        <table style={styles.table}>
          <thead><tr>{["Symbol","Quality","Source"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>
            {samples.map(s => (
              <tr key={s.id}><td style={styles.td}>{s.symbol}</td><td style={styles.td}>{fmtNum(s.qualityScore)}</td><td style={styles.td}>{s.sourceType}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Training Panel ── */
/* ── Auto-Training Status Banner ── */
function AutoTrainingStatusBanner() {
  const [status, setStatus] = useState<xgbApi.AutoTrainingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const s = await xgbApi.getAutoTrainingStatus();
      setStatus(s);
    } catch {
      // Silently keep last known state; getTrainingRuns rarely fails
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  if (loading && !status) {
    return (
      <div style={{ marginBottom: "16px", padding: "12px 16px", borderRadius: "8px", background: "rgba(30,41,59,0.6)", border: "1px solid rgba(148,163,184,0.15)" }}>
        <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>Auto-training status loading...</span>
      </div>
    );
  }

  if (!status) {
    return (
      <div style={{ marginBottom: "16px", padding: "12px 16px", borderRadius: "8px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
        <span style={{ fontSize: "0.8rem", color: "#f87171" }}>Could not load training status. Check server logs.</span>
      </div>
    );
  }

  const lastRun = status.lastTrainingRun;
  const dotColor = lastRun?.status === "running" ? "#facc15" : status.enabled ? "#22c55e" : "#ef4444";
  const bgColor = lastRun?.status === "running"
    ? "rgba(250,204,21,0.06)" : status.enabled ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.06)";
  const borderColor = lastRun?.status === "running"
    ? "rgba(250,204,21,0.2)" : status.enabled ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)";

  return (
    <div style={{
      marginBottom: "16px", padding: "12px 16px", borderRadius: "8px",
      background: bgColor, border: `1px solid ${borderColor}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: dotColor }} />
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#f1f5f9" }}>
            Auto-Training: {lastRun?.status === "running" ? "RUNNING" : "MONITORING"}
          </span>
          {status.timeSinceLastRunDisplay && status.timeSinceLastRunDisplay !== "never" && (
            <span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.6)" }}>
              Last: {status.timeSinceLastRunDisplay}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "16px", fontSize: "0.75rem", color: "rgba(203,213,225,0.5)" }}>
          {status.trainingRuns24h != null && (
            <span>{status.trainingRuns24h} runs / 24h</span>
          )}
          <span>Refresh: 30s</span>
        </div>
      </div>
      {lastRun && (
        <div style={{ marginTop: "8px", fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <span>Latest: {lastRun.status === "completed" ? "Done" : lastRun.status === "running" ? "Running..." : lastRun.status}</span>
          {lastRun.startedAt && (
            <span>{new Date(lastRun.startedAt).toLocaleString()}</span>
          )}
          {lastRun.sampleCount != null && (
            <span>{lastRun.sampleCount.toLocaleString()} samples</span>
          )}
          {lastRun.candidateVersion && (
            <span>v{lastRun.candidateVersion}</span>
          )}
        </div>
      )}
      <div style={{ marginTop: "6px", fontSize: "0.72rem", color: "rgba(203,213,225,0.45)" }}>
        {status.message}
      </div>
    </div>
  );
}

function TrainingPanel() {
  const [runs, setRuns] = useState<TrainingRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const logAfterLineRef = useRef(0);
  const [viewingLogs, setViewingLogs] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);
  const wasRunningRef = useRef(false);

  useEffect(() => {
    if (!document.getElementById("xgboost-training-log-pulse")) {
      const style = document.createElement("style");
      style.id = "xgboost-training-log-pulse";
      style.textContent = `@keyframes xgbPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`;
      document.head.appendChild(style);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const data = await xgbApi.getTrainingRuns();
      setRuns(data.runs || []);
      setError(null);
    } catch (e) {
      logger.error("Failed to load training runs", e as Error);
      setError("Failed to load training runs");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTrigger = useCallback(async (mode: string, refresh: boolean, modelTypeParam?: string, maxSamplesVal?: number) => {
    setTriggering(true);
    try {
      const result = await xgbApi.triggerTraining(mode, refresh, undefined, modelTypeParam || "legacy", maxSamplesVal);
      if (result.success) {
        setActiveRunId(result.id || null);
        setLogLines([]);
        logAfterLineRef.current = 0;
        setViewingLogs(true);
        setLoading(true);
      }
    } catch (e) {
      logger.error("Failed to trigger training", e as Error);
    } finally {
      setTriggering(false);
    }
  }, []);

  const handleAutoTrain = useCallback(async () => {
    setTriggering(true);
    try {
      const result = await xgbApi.triggerAutoTrain(1);
      if (result.success && result.id) {
        setActiveRunId(result.id);
        setLogLines([]);
        logAfterLineRef.current = 0;
        setViewingLogs(true);
        setLoading(true);
      }
    } catch (e) {
      logger.error("Failed to trigger auto-train", e as Error);
    } finally {
      setTriggering(false);
    }
  }, []);

  const [modelType, setModelType] = useState<string>("v7");
  const [maxSamples, setMaxSamples] = useState<number>(100000);

  useEffect(() => {
    if (!activeRunId || !viewingLogs) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await xgbApi.getTrainingRuns();
        const run = (data.runs || []).find((r) => r.id === activeRunId);
        const logs = await xgbApi.getTrainingLogs(activeRunId, 0);
        if (!cancelled) {
          setLogLines(logs.lines);
          logAfterLineRef.current = logs.totalLines;
          const isFinished = run && run.status !== "running" && run.status !== "pending";
          if (wasRunningRef.current && isFinished) {
            setActiveRunId(null);
            loadRuns();
          }
          if (run && (run.status === "running" || run.status === "pending")) {
            wasRunningRef.current = true;
          }
        }
      } catch {
        // ignore poll errors
      }
    };
    wasRunningRef.current = false;
    poll();
    const interval = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeRunId, viewingLogs]);

  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logLines, autoScroll]);

  useEffect(() => {
    loadRuns();
    const interval = setInterval(loadRuns, 8000);
    return () => clearInterval(interval);
  }, [loadRuns]);

  return (
    <div>
      {/* Auto-Training Status Banner */}
      <AutoTrainingStatusBanner />
      <div style={{ marginBottom: "20px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <Button
          type="button"
          variant="primary"
          size="small"
          onClick={() => handleTrigger("scalp", false, modelType, maxSamples)}
          disabled={triggering || viewingLogs}
        >
          {triggering ? "Starting..." : "Full Training (scalp)"}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="small"
          onClick={() => handleAutoTrain()}
          disabled={triggering || viewingLogs}
          style={{ background: "linear-gradient(135deg, #8B5CF6, #6366F1)" }}
        >
          Quick Train M28 (Daily Decisions)
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="small"
          onClick={() => handleTrigger("scalp", true, modelType, maxSamples)}
          disabled={triggering || viewingLogs}
          style={{ border: "1px solid rgba(148,163,184,0.3)" }}
        >
          Refresh Training (scalp)
        </Button>
        <span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.5)" }}>
          "Quick Train M28" labels decisions then trains M28 champion model.
        </span>
      </div>

      {/* Model type selector */}
      <div style={{ marginBottom: "16px", display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", padding: "6px 12px", borderRadius: "6px", background: modelType === "v7" ? "rgba(34,197,94,0.15)" : "rgba(148,163,184,0.08)", border: `1px solid ${modelType === "v7" ? "rgba(34,197,94,0.4)" : "rgba(148,163,184,0.2)"}` }}>
          <input type="radio" name="modelType" value="v7" checked={modelType === "v7"} onChange={() => setModelType("v7")} style={{ accentColor: "#22c55e" }} />
          <span style={{ fontSize: "0.8rem", color: modelType === "v7" ? "#22c55e" : "#94a3b8", fontWeight: modelType === "v7" ? 600 : 400 }}>
            V7 Binary WIN/LOSE (~10min)
          </span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", padding: "6px 12px", borderRadius: "6px", background: modelType === "legacy" ? "rgba(59,130,246,0.15)" : "rgba(148,163,184,0.08)", border: `1px solid ${modelType === "legacy" ? "rgba(59,130,246,0.4)" : "rgba(148,163,184,0.2)"}` }}>
          <input type="radio" name="modelType" value="legacy" checked={modelType === "legacy"} onChange={() => setModelType("legacy")} style={{ accentColor: "#3b82f6" }} />
          <span style={{ fontSize: "0.8rem", color: modelType === "legacy" ? "#3b82f6" : "#94a3b8", fontWeight: modelType === "legacy" ? 600 : 400 }}>
            Legacy Ensemble (45 models, ~6h)
          </span>
        </label>
      </div>

      {/* Max samples input */}
      <div style={{ marginBottom: "16px", display: "flex", gap: "12px", alignItems: "center" }}>
        <label style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
          Max Samples:
        </label>
        <input
          type="number"
          value={maxSamples}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val >= 5000 && val <= 500000) {
              setMaxSamples(val);
            }
          }}
          min={5000}
          max={500000}
          step={10000}
          style={{
            width: "120px",
            padding: "6px 10px",
            fontSize: "0.85rem",
            color: "#f1f5f9",
            background: "#0f172a",
            border: "1px solid rgba(148,163,184,0.3)",
            borderRadius: "6px",
            textAlign: "center",
          }}
        />
        <span style={{ fontSize: "0.7rem", color: "rgba(148,163,184,0.5)" }}>
          (5,000 - 500,000)
        </span>
      </div>

      {viewingLogs && activeRunId && (
        <div style={{ marginBottom: "20px", background: "#0f172a", borderRadius: "8px", border: "1px solid rgba(148,163,184,0.2)", overflow: "hidden" }}>
          <div style={{ padding: "8px 16px", background: "rgba(30,41,59,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#eab308", animation: "xgbPulse 1.5s infinite" }} />
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#f1f5f9" }}>Training Log</span>
              <span style={{ fontSize: "0.7rem", color: "rgba(203,213,225,0.4)", fontFamily: "monospace" }}>{activeRunId.slice(0, 12)}...</span>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontSize: "0.72rem", color: "rgba(203,213,225,0.6)", cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} style={{ marginRight: "4px", cursor: "pointer" }} />
                Auto-scroll
              </label>
              <Button type="button" variant="ghost" size="small" onClick={() => { setViewingLogs(false); setActiveRunId(null); }}>
                Close
              </Button>
            </div>
          </div>
          <div
            ref={logRef}
            style={{
              padding: "14px 16px",
              maxHeight: "420px",
              minHeight: "120px",
              overflow: "auto",
              fontFamily: '"Cascadia Code", "Fira Code", "Consolas", "Courier New", monospace',
              fontSize: "0.76rem",
              lineHeight: 1.55,
              color: "#94a3b8",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              background: "#0b1120",
            }}
          >
            {logLines.length === 0 ? (
              <div style={{ color: "rgba(148,163,184,0.4)", fontStyle: "italic", padding: "20px 0", textAlign: "center" }}>
                Waiting for training to produce output...
              </div>
            ) : (
              logLines.map((line, i) => (
                <div key={i} style={{ minHeight: "1.55em" }}>
                  {line || "\u00A0"}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <h3 style={styles.sectionTitle}>Training History</h3>
      {loading && runs.length === 0 ? (
        <div style={{ padding: "24px" }}><Skeleton /></div>
      ) : error && runs.length === 0 ? (
        <ErrorState message={error} onRetry={loadRuns} />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Started</th>
                <th style={styles.th}>Completed</th>
                <th style={styles.th}>Samples</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={styles.emptyState}>No training runs yet</td>
                </tr>
              ) : (
                runs.map((run) => (
                  <tr key={run.id}>
                    <td style={styles.td}><StatusBadge status={run.status} /></td>
                    <td style={styles.td}>{fmtDate(run.startedAt)}</td>
                    <td style={styles.td}>{fmtDate(run.completedAt)}</td>
                    <td style={styles.td}>{run.sampleCount ?? "-"}</td>
                    <td style={styles.td}>{run.candidateVersion ?? "-"}</td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          onClick={() => {
                            setActiveRunId(run.id);
                            setLogLines([]);
                            logAfterLineRef.current = 0;
                            setViewingLogs(true);
                            xgbApi.getTrainingLogs(run.id, 0).then((logs) => {
                              setLogLines(logs.lines);
                              logAfterLineRef.current = logs.totalLines;
                            }).catch(() => {});
                          }}
                        >
                          View Log
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          style={{ color: "#ef4444" }}
                          onClick={async () => {
                            if (!confirm(`Delete training run ${run.id.slice(0, 12)}...?`)) return;
                            try {
                              await xgbApi.deleteTrainingRun(run.id);
                              loadRuns();
                            } catch (e) {
                              logger.error("Failed to delete training run", e as Error);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
/* ── Models Panel ── */
function ModelsPanel() {
  const [models, setModels] = useState<ModelRegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadModels = useCallback(async () => {
    try {
      const data = await xgbApi.getModels();
      setModels(data.models || []);
      setError(null);
    } catch (e) {
      logger.error("Failed to load models", e as Error);
      setError("Failed to load models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);

  if (loading && models.length === 0) return <div style={{ padding: "24px" }}><Skeleton /></div>;
  if (error && models.length === 0) return <ErrorState message={error} onRetry={loadModels} />;

  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        <span style={{ fontSize: "0.8rem", color: "rgba(203,213,225,0.5)" }}>
          {models.length} model{models.length !== 1 ? "s" : ""} registered
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Version</th>
              <th style={styles.th}>Trained</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}>Scalp Mode</th>
              <th style={styles.th}>Threshold</th>
              <th style={styles.th}>Samples</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.id} style={m.isActive ? { background: "rgba(34,197,94,0.06)" } : undefined}>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, color: m.isActive ? "#22c55e" : "#6b7280", background: m.isActive ? "rgba(34,197,94,0.15)" : "rgba(107,114,128,0.15)", fontWeight: 600, textTransform: "uppercase" as const }}>{m.role}{m.isActive ? " ★" : ""}</span>
                </td>
                <td style={styles.td}>{m.version}</td>
                <td style={styles.td}>{m.trainedAt ? fmtDate(m.trainedAt) : "-"}</td>
                <td style={styles.td}>{m.isActive ? <span style={{ color: "#22c55e", fontWeight: 600 }}>Active</span> : "No"}</td>
                <td style={styles.td}>{m.isScalpMode ? "Yes" : "No"}</td>
                <td style={styles.td}>{m.thresholdDefault != null ? fmtNum(m.thresholdDefault, 2) : "-"}</td>
                <td style={styles.td}>{m.metricsJson?.total_training_samples as number ?? "-"}</td>
                <td style={styles.td}>
                  <div style={{ display: "flex", gap: "6px" }}>
                    {m.role === "challenger" && !m.isActive && (
                      <Button type="button" variant="ghost" size="small" style={{ color: "#22c55e" }}
                        onClick={async () => {
                          if (!confirm("Promote this challenger to champion?")) return;
                          try { await xgbApi.promoteChallenger(m.id); loadModels(); }
                          catch (e) { logger.error("Promote failed", e as Error); }
                        }}>Promote</Button>
                    )}
                    {m.role !== "champion" && (
                      <Button type="button" variant="ghost" size="small" style={{ color: "#eab308" }}
                        onClick={async () => {
                          if (!confirm("Rollback to this model?")) return;
                          try { await xgbApi.rollbackModel(m.id); loadModels(); }
                          catch (e) { logger.error("Rollback failed", e as Error); }
                        }}>Rollback</Button>
                    )}
                    <Button type="button" variant="ghost" size="small" style={{ color: "#ef4444" }}
                      onClick={async () => {
                        if (!confirm(`Delete model ${m.version} (${m.role})?`)) return;
                        try { await xgbApi.deleteModel(m.id); loadModels(); }
                        catch (e) { logger.error("Delete failed", e as Error); }
                      }}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
/* ── Drift Panel ── */
function DriftPanel() {
  const [snapshots, setSnapshots] = useState<DriftSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [computing, setComputing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await xgbApi.getDriftSnapshots();
      setSnapshots(data.snapshots);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load drift data");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshDriftSilently = useCallback(async () => {
    try {
      const data = await xgbApi.getDriftSnapshots();
      setSnapshots(data.snapshots);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const interval = setInterval(refreshDriftSilently, 30000);
    return () => clearInterval(interval);
  }, [refreshDriftSilently]);

  const handleCompute = async () => {
    setComputing(true);
    try {
      await xgbApi.computeDrift();
      await refreshDriftSilently();
    } catch {}
    setComputing(false);
  };

  const latest = snapshots.length > 0 ? snapshots[0] : null;
  const latestMeta = latest?.metadataJson ?? {};
  const recentCritical = snapshots.filter((s) => s.alertLevel === "critical").length;

  return (
    <div>
      <InfoBanner
        title="How drift monitoring works"
        paragraphs={[
          "Drift measures how the model's decision distribution changes over time. A high pass-rate drift or score drift means the model is seeing different market conditions than when it was trained.",
          "The system compares the last 7 days of decisions against a 30-day baseline. If the pass rate drops by more than 15%, a warning is raised; more than 30% is critical.",
          "Compute a drift snapshot manually, or check the table for historical trends. Top shifted features show which indicators changed most between windows.",
        ]}
      />
      <div style={{ marginBottom: "16px" }}>
        <Button variant="primary" size="small" onClick={handleCompute} disabled={computing}>
          {computing ? "Computing..." : "Compute Drift Snapshot"}
        </Button><HelpTip text="Compute a new drift snapshot comparing current 7-day window against 30-day baseline. Requires at least 10 decisions in each window." />
      </div>

      {latest && (
        <div style={{ ...styles.cardsRow, marginBottom: "16px" }}>
          <div style={{ ...styles.statCard, borderColor: latest.alertLevel === "critical" ? "rgba(239,68,68,0.3)" : latest.alertLevel === "warning" ? "rgba(234,179,8,0.3)" : "rgba(34,197,94,0.3)" }}>
            <div style={styles.statLabel}>Alert Level</div>
            <div style={{ ...styles.statValue, fontSize: "1rem", color: getAlertColor(latest.alertLevel) }}>{latest.alertLevel ?? "ok"}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Pass Rate Drift</div>
            <div style={{ ...styles.statValue, fontSize: "1.2rem", color: latest.passRateDrift && latest.passRateDrift > 0.15 ? "#ef4444" : "#22c55e" }}>
              {latest.passRateDrift !== null && latest.passRateDrift !== undefined ? `${(latest.passRateDrift * 100).toFixed(1)}%` : "-"}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Score Drift</div>
            <div style={{ ...styles.statValue, fontSize: "1.2rem", color: latest.scoreDistributionDrift && latest.scoreDistributionDrift > 0.1 ? "#ef4444" : "#22c55e" }}>
              {latest.scoreDistributionDrift !== null && latest.scoreDistributionDrift !== undefined ? latest.scoreDistributionDrift.toFixed(4) : "-"}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Feature Drift Score</div>
            <div style={{ ...styles.statValue, fontSize: "1.2rem", color: latest.featureDriftScore && latest.featureDriftScore > 0.1 ? "#ef4444" : "#f1f5f9" }}>
              {latest.featureDriftScore !== null && latest.featureDriftScore !== undefined ? latest.featureDriftScore.toFixed(4) : "-"}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Window</div>
            <div style={{ ...styles.statValue, fontSize: "1rem", color: "#f1f5f9" }}>{latest.windowLabel ?? "-"}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Critical Alerts (total)</div>
            <div style={{ ...styles.statValue, fontSize: "1.2rem", color: recentCritical > 0 ? "#ef4444" : "#22c55e" }}>{recentCritical}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Sample Size</div>
            <div style={{ ...styles.statValue, fontSize: "1.2rem", color: "#f1f5f9" }}>{(latestMeta as any).sampleSize ?? "-"}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Avg Score</div>
            <div style={{ ...styles.statValue, fontSize: "1.2rem", color: "#f1f5f9" }}>
              {(latestMeta as any).avgScore != null ? ((latestMeta as any).avgScore as number).toFixed(4) : "-"}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
      ) : error ? (
        <ErrorState message={error} onRetry={loadData} />
      ) : snapshots.length === 0 ? (
        <EmptyState message="No drift snapshots computed yet." />
      ) : (
        <>
          <div style={{ overflowX: "auto" }} className="xgb-table">
            <table style={styles.table}>
              <thead>
                <tr>
                  {["Time", "Window", "Alert", "Samples", "Avg Score", "Pass Rate", "Feature Drift", "Score Drift", "Top Shifted"].map((h) => <th key={h} style={styles.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => {
                  const meta = s.metadataJson ?? {};
                  const shiftedFeatures = (meta as any).topShiftedFeatures ?? (meta as any).top_shifted_features;
                  return (
                    <tr key={s.id}>
                      <td style={styles.td}>{fmtDate(s.computedAt)}</td>
                      <td style={styles.td}>{s.windowLabel ?? "-"}</td>
                      <td style={{ ...styles.td, color: getAlertColor(s.alertLevel), fontWeight: 600 }}>{s.alertLevel ?? "ok"}</td>
                      <td style={styles.td}>{(meta as any).sampleSize ?? "-"}</td>
                      <td style={styles.td}>{fmtNum((meta as any).avgScore, 4)}</td>
                      <td style={styles.td}>{(meta as any).passRate != null ? `${((meta as any).passRate as number) * 100}%` : "-"}</td>
                      <td style={{ ...styles.td, color: (s.featureDriftScore ?? 0) > 0.1 ? "#ef4444" : "rgba(203,213,225,0.6)" }}>{fmtNum(s.featureDriftScore, 4)}</td>
                      <td style={{ ...styles.td, color: (s.scoreDistributionDrift ?? 0) > 0.1 ? "#ef4444" : "rgba(203,213,225,0.6)" }}>{fmtNum(s.scoreDistributionDrift, 4)}</td>
                      <td style={styles.td}>
                        {shiftedFeatures ? (
                          <span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.6)", cursor: "pointer" }}
                            title={Array.isArray(shiftedFeatures) ? (shiftedFeatures as string[]).join(", ") : String(shiftedFeatures)}>
                            {Array.isArray(shiftedFeatures) ? `${(shiftedFeatures as string[]).length} features` : "yes"}
                          </span>
                        ) : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {snapshots.some((s) => {
            const meta = s.metadataJson ?? {};
            const shifted = (meta as any).topShiftedFeatures ?? (meta as any).top_shifted_features;
            return Array.isArray(shifted) && shifted.length > 0;
          }) && (
            <div style={{ marginTop: "16px", padding: "12px", borderRadius: "8px", background: "rgba(30,41,59,0.6)", border: "1px solid rgba(148,163,184,0.12)" }}>
              <h4 style={{ color: "#f1f5f9", margin: "0 0 8px", fontSize: "0.9rem" }}>Top Shifted Features (latest snapshot)</h4>
              <pre style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", maxHeight: "200px", overflow: "auto", margin: 0 }}>
                {JSON.stringify(snapshots[0]?.metadataJson?.topShiftedFeatures ?? snapshots[0]?.metadataJson?.top_shifted_features ?? [], null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Backfill Panel ── */
function BackfillPanel() {
  const [jobs, setJobs] = useState<BackfillJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [multiTfCreating, setMultiTfCreating] = useState(false);
  const [liveProgress, setLiveProgress] = useState<BackfillProgress | null>(null);
  const [formData, setFormData] = useState({
    marketType: "spot",
    symbolsPreset: "top10",
    symbolsCustom: "BTC/USDT,ETH/USDT,SOL/USDT",
    timeframesPreset: "1m,5m,15m",
    timeframesCustom: "",
    requestedSamples: 5000,
    lookbackDays: 90,
    parallelMode: true,
    numShards: 5,
    candleTf: "5m",
  });

  const TIMEFRAME_PRESETS: Record<string, string> = {
    "1m": "1m only",
    "1m,5m": "Scalp (1m, 5m)",
    "1m,5m,15m": "Standard (1m, 5m, 15m)",
    "1m,5m,15m,1h": "All (1m, 5m, 15m, 1h)",
    "custom": "Custom",
  };

  const SYMBOL_PRESETS: Record<string, { label: string; symbols: string }> = {
    top10: { label: "Top 10", symbols: "BTC/USDT,ETH/USDT,SOL/USDT,BNB/USDT,XRP/USDT,ADA/USDT,AVAX/USDT,DOT/USDT,LINK/USDT,MATIC/USDT" },
    top15: { label: "Top 15", symbols: "BTC/USDT,ETH/USDT,SOL/USDT,BNB/USDT,XRP/USDT,ADA/USDT,AVAX/USDT,DOT/USDT,LINK/USDT,MATIC/USDT,NEAR/USDT,ARB/USDT,OP/USDT,FTM/USDT,ATOM/USDT" },
    top20: { label: "Top 20", symbols: "BTC/USDT,ETH/USDT,SOL/USDT,BNB/USDT,XRP/USDT,ADA/USDT,AVAX/USDT,DOT/USDT,LINK/USDT,MATIC/USDT,NEAR/USDT,ARB/USDT,OP/USDT,FTM/USDT,ATOM/USDT,LDO/USDT,AAVE/USDT,UNI/USDT,FET/USDT,PEPE/USDT" },
    top30: { label: "Top 30", symbols: "BTC/USDT,ETH/USDT,SOL/USDT,BNB/USDT,XRP/USDT,ADA/USDT,AVAX/USDT,DOT/USDT,LINK/USDT,MATIC/USDT,NEAR/USDT,ARB/USDT,OP/USDT,FTM/USDT,ATOM/USDT,LDO/USDT,AAVE/USDT,UNI/USDT,FET/USDT,PEPE/USDT,INJ/USDT,APT/USDT,SEI/USDT,TIA/USDT,STRK/USDT,WIF/USDT,DOGE/USDT,SUI/USDT,PENDLE/USDT" },
    our_pairs: { label: "Our Traded Pairs", symbols: "TIA/USDT,ALCH/USDT,ENA/USDT,PROVE/USDT,HYPE/USDT,DYDX/USDT,BASED/USDT,BBSOL/USDT,PNUT/USDT,PYTH/USDT,PARTI/USDT,F/USDT,CRCLX/USDT" },
    custom: { label: "Custom", symbols: "" },
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await xgbApi.getBackfillJobs();
      setJobs(data.jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load backfill jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshJobsSilently = useCallback(async () => {
    try {
      const data = await xgbApi.getBackfillJobs();
      setJobs(data.jobs);
    } catch {
      /* silent */
    }
  }, []);

  const fetchLiveProgress = useCallback(async (jobId: string) => {
    try {
      const progress = await xgbApi.getBackfillProgress(jobId);
      setLiveProgress(progress);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const interval = setInterval(refreshJobsSilently, 3000);
    return () => clearInterval(interval);
  }, [refreshJobsSilently]);

  const runningJob = jobs.find(j => j.status === "running");

  useEffect(() => {
    if (runningJob) {
      const interval = setInterval(() => fetchLiveProgress(runningJob.id), 2000);
      fetchLiveProgress(runningJob.id);
      return () => clearInterval(interval);
    }
    setLiveProgress(null);
  }, [runningJob?.id, fetchLiveProgress]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const symbolsList = (
        formData.symbolsPreset === "custom"
          ? formData.symbolsCustom
          : SYMBOL_PRESETS[formData.symbolsPreset]?.symbols ?? ""
      ).split(",").map((s) => s.trim()).filter(Boolean);
      const timeframesList = (
        formData.timeframesPreset === "custom"
          ? formData.timeframesCustom
          : formData.timeframesPreset
      ).split(",").map((t) => t.trim()).filter(Boolean);
      const toDt = new Date().toISOString();
      const fromDt = new Date(Date.now() - formData.lookbackDays * 86400000).toISOString();

      const result = await xgbApi.createBackfillJob({
        marketType: formData.marketType,
        symbolsScope: { symbols: symbolsList, source: "custom", maxSymbols: symbolsList.length },
        timeframes: timeframesList,
        fromDatetime: fromDt,
        toDatetime: toDt,
        requestedSamples: formData.requestedSamples,
        filtersJson: {
          parallel: formData.parallelMode,
          numShards: formData.parallelMode ? formData.numShards : 1,
          candle_tf: formData.candleTf,
        },
      });
      if (result.success) {
        setShowForm(false);
        await loadData();
      }
    } catch {}
    setCreating(false);
  };

  const handleMultiTfCreate = async () => {
    setMultiTfCreating(true);
    try {
      const result = await xgbApi.createMultiTfBackfill();
      if (result.success) {
        await loadData();
      } else {
        alert(`Multi-TF backfill failed: ${result.message}`);
      }
    } catch (err: any) {
      alert(`Error: ${err?.message || String(err)}`);
    }
    setMultiTfCreating(false);
  };

  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatNumber = (n: number): string => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  const badgeStyle = (status: string) => {
    const colors: Record<string, string> = {
      completed: "#22c55e", failed: "#ef4444", running: "#3b82f6",
    };
    return {
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "2px 8px", borderRadius: "10px", fontSize: "0.7rem",
      fontWeight: 600, color: colors[status] ?? "#94a3b8",
      background: `${colors[status] ?? "#94a3b8"}15`,
    };
  };

  return (
    <div>
      <InfoBanner
        title="How backfill generates training data (v2 with parallel sharding)"
        paragraphs={[
          "Backfill v2 uses CUSUM event detection + Triple-Barrier labeling (with intra-candle high/low detection) to generate accurate labeled samples. Fixes the previous bug where only close prices were used for barrier detection.",
          "Each sample includes 287 features computed from 1m candles + multi-timeframe signals (5m, 15m, 1h), stored with TP/SL/timeout labels ready for filter model training.",
          "Parallel mode splits the symbol list into 5 shards running simultaneously, achieving ~5x speedup. Live progress for each shard is shown below.",
          "Monitor progress below. A full 90-day backfill across 400 symbols with 5 shards typically completes in 15-30 minutes.",
        ]}
      />
      <div style={{ marginBottom: "16px", display: "flex", gap: "12px", alignItems: "center" }}>
        <Button variant="primary" size="small" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "New Backfill Job"}
        </Button><HelpTip text="Create a new backfill job to generate labeled samples from historical exchange data. Runs in the background." />
        <Button variant="ghost" size="small" onClick={loadData} disabled={loading}>
          Refresh
        </Button><HelpTip text="Refresh the jobs list to see updated progress on running jobs." />
      </div>

      {showForm && (
        <div style={{
          padding: "20px", borderRadius: "8px", marginBottom: "20px",
          background: "rgba(30,41,59,0.8)", border: "1px solid rgba(148,163,184,0.15)",
        }}>
          <h4 style={{ margin: "0 0 16px", color: "#f1f5f9", fontSize: "1rem" }}>Create Backfill Job</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", marginBottom: "4px" }}>Market Type</label>
              <select value={formData.marketType} onChange={(e) => setFormData({ ...formData, marketType: e.target.value })}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
                <option value="spot">Spot</option>
                <option value="futures">Futures</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", marginBottom: "4px" }}>Timeframes</label>
              <select value={formData.timeframesPreset} onChange={(e) => setFormData({ ...formData, timeframesPreset: e.target.value })}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
                {Object.entries(TIMEFRAME_PRESETS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              {formData.timeframesPreset === "custom" && (
                <input type="text" value={formData.timeframesCustom} onChange={(e) => setFormData({ ...formData, timeframesCustom: e.target.value })}
                  placeholder="1m,5m,15m,1h"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", marginTop: "4px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }} />
              )}
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", marginBottom: "4px" }}>Candle TF (CUSUM)</label>
              <select value={formData.candleTf} onChange={(e) => setFormData({ ...formData, candleTf: e.target.value })}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
                <option value="1m">1m (granular)</option>
                <option value="5m">5m (cleaner)</option>
                <option value="15m">15m</option>
                <option value="1h">1h (macro)</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", marginBottom: "4px" }}>Target Samples</label>
              <input type="number" min={100} max={500000} value={formData.requestedSamples}
                onChange={(e) => setFormData({ ...formData, requestedSamples: parseInt(e.target.value) || 500 })}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", marginBottom: "4px" }}>Lookback (days)</label>
              <input type="number" min={1} max={365} value={formData.lookbackDays}
                onChange={(e) => setFormData({ ...formData, lookbackDays: parseInt(e.target.value) || 30 })}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }} />
            </div>
          </div>
          <div style={{ marginTop: "12px" }}>
            <label style={{ display: "block", fontSize: "0.8rem", color: "rgba(203,213,225,0.7)", marginBottom: "4px" }}>Symbols</label>
            <select value={formData.symbolsPreset} onChange={(e) => setFormData({ ...formData, symbolsPreset: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }}>
              {Object.entries(SYMBOL_PRESETS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {formData.symbolsPreset === "custom" && (
              <input type="text" value={formData.symbolsCustom} onChange={(e) => setFormData({ ...formData, symbolsCustom: e.target.value })}
                placeholder="BTC/USDT,ETH/USDT,SOL/USDT"
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", marginTop: "4px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)" }} />
            )}
          </div>
          <div style={{ marginTop: "16px", padding: "12px", borderRadius: "6px", background: "rgba(15,23,42,0.4)", border: "1px solid rgba(148,163,184,0.1)" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formData.parallelMode}
                onChange={(e) => setFormData({ ...formData, parallelMode: e.target.checked })}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              <span style={{ fontSize: "0.85rem", color: "#f1f5f9", fontWeight: 600 }}>Parallel Mode (5 Shards)</span>
              <HelpTip text="Splits the symbol list into 5 groups and processes them simultaneously in separate processes for ~5x faster backfill. Default: ON." />
            </label>
            {formData.parallelMode && (
              <div style={{ marginTop: "8px", marginLeft: "24px" }}>
                <label style={{ display: "block", fontSize: "0.75rem", color: "rgba(203,213,225,0.6)", marginBottom: "2px" }}>Number of Shards</label>
                <input
                  type="number"
                  min={2}
                  max={10}
                  value={formData.numShards}
                  onChange={(e) => setFormData({ ...formData, numShards: parseInt(e.target.value) || 5 })}
                  style={{ width: "80px", padding: "6px 10px", borderRadius: "6px", background: "rgba(15,23,42,0.6)", color: "#f1f5f9", border: "1px solid rgba(148,163,184,0.2)", fontSize: "0.85rem" }}
                />
              </div>
            )}
          </div>
          <div style={{ marginTop: "16px" }}>
            <Button variant="primary" size="small" onClick={handleCreate} disabled={creating}>
              {creating ? "Creating..." : "Start Backfill"}
            </Button>
          </div>
          <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid rgba(148,163,184,0.1)" }}>
            <Button
              variant="primary"
              size="small"
              onClick={handleMultiTfCreate}
              disabled={multiTfCreating}
              style={{
                background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                border: "1px solid rgba(168,85,247,0.4)",
              }}
            >
              {multiTfCreating ? "Launching..." : "Multi-TF Backfill (5m + 15m + 1h)"}
            </Button>
            <div style={{ marginTop: "6px", fontSize: "0.7rem", color: "rgba(203,213,225,0.5)" }}>
              3-phase automated backfill: 5m scalp (TP=1.0%, SL=1.6%), 15m swing (TP=1.5%, SL=2.0%), 1h trend (TP=2.0%, SL=2.5%). All ~500 Bybit USDT symbols.
            </div>
          </div>
        </div>
      )}

      {runningJob && liveProgress && liveProgress.shards.length > 0 && (
        <div style={{
          padding: "20px", borderRadius: "10px", marginBottom: "20px",
          background: "rgba(30,41,59,0.9)", border: "1px solid rgba(59,130,246,0.25)",
          boxShadow: "0 0 20px rgba(59,130,246,0.08)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div>
              <h4 style={{ margin: 0, color: "#f1f5f9", fontSize: "1rem" }}>Backfill In Progress</h4>
              <span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.6)" }}>
                {formatElapsed(liveProgress.elapsedSeconds)} elapsed
              </span>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#3b82f6" }}>
                {formatNumber(liveProgress.totalSamples)}
              </div>
              <span style={{ fontSize: "0.7rem", color: "rgba(203,213,225,0.6)" }}>samples collected</span>
            </div>
          </div>

          {/* Overall progress bar */}
          <div style={{
            height: "12px", borderRadius: "6px", background: "rgba(148,163,184,0.12)",
            overflow: "hidden", marginBottom: "16px", position: "relative",
          }}>
            <div style={{
              height: "100%", width: `${Math.min(100, liveProgress.overallPct)}%`,
              borderRadius: "6px", transition: "width 1.2s ease-out",
              background: "linear-gradient(90deg, #3b82f6, #06b6d4)",
              boxShadow: "0 0 12px rgba(59,130,246,0.3)",
            }} />
            <span style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.75rem", fontWeight: 700, color: liveProgress.overallPct > 30 ? "#fff" : "#94a3b8",
            }}>
              {liveProgress.overallPct.toFixed(1)}%
            </span>
          </div>

          {/* Per-shard mini bars */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(liveProgress.shards.length, 5)}, 1fr)`, gap: "10px" }}>
            {liveProgress.shards.map((shard) => {
              const pct = shard.totalSymbols > 0 ? (shard.symbolsProcessed / shard.totalSymbols) * 100 : 0;
              const statusColor =
                shard.status === "completed" ? "#22c55e" :
                shard.status === "failed" ? "#ef4444" :
                shard.status === "running" ? "#3b82f6" : "#475569";
              return (
                <div key={shard.shardId} style={{
                  padding: "12px", borderRadius: "8px",
                  background: "rgba(15,23,42,0.5)", border: `1px solid ${statusColor}20`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <span style={{ fontSize: "0.75rem", fontWeight: 600, color: statusColor }}>Shard {shard.shardId}</span>
                    <span style={badgeStyle(shard.status)}>
                      {shard.status === "running" ? "\u25B6" : shard.status === "completed" ? "\u2713" : shard.status === "failed" ? "\u2717" : "\u25CB"} {shard.status}
                    </span>
                  </div>
                  <div style={{
                    height: "8px", borderRadius: "4px", background: "rgba(148,163,184,0.1)",
                    overflow: "hidden", marginBottom: "6px",
                  }}>
                    <div style={{
                      height: "100%", width: `${Math.min(100, pct)}%`,
                      borderRadius: "4px", transition: "width 0.8s ease-out",
                      background: shard.status === "completed" ? "linear-gradient(90deg, #22c55e, #16a34a)" :
                        shard.status === "failed" ? "#ef4444" : "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                    }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", color: "rgba(203,213,225,0.6)" }}>
                    <span>{shard.symbolsProcessed}/{shard.totalSymbols} symbols</span>
                    <span>{formatNumber(shard.samplesCollected)} samples</span>
                  </div>
                  {shard.currentSymbol && shard.status === "running" && (
                    <div style={{ marginTop: "4px", fontSize: "0.65rem", color: "rgba(203,213,225,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {shard.currentSymbol}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed/failed banner */}
      {runningJob && (!liveProgress || liveProgress.shards.length === 0) && (
        <div style={{
          padding: "14px 20px", borderRadius: "8px", marginBottom: "20px",
          background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)",
          display: "flex", alignItems: "center", gap: "10px",
        }}>
          <div style={{
            width: "16px", height: "16px", borderRadius: "50%", border: "2px solid #3b82f6",
            borderTopColor: "transparent", animation: "spin 1s linear infinite",
          }} />
          <span style={{ fontSize: "0.85rem", color: "#93c5fd" }}>Backfill starting up... initializing shards...</span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "rgba(203,213,225,0.5)" }}>Loading jobs...</div>
      ) : error ? (
        <ErrorState message={error} onRetry={loadData} />
      ) : jobs.length === 0 ? (
        <EmptyState message="No backfill jobs created yet. Use the form above to start one." />
      ) : (
        <div style={{ overflowX: "auto" }} className="xgb-table">
          <table style={styles.table}>
            <thead>
              <tr>
                {["Status", "Created", "Market", "Timeframes", "Requested", "Collected", "Progress", "Error", "Actions"].map((h) => <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const isRunning = j.status === "running";
                const progressPct = isRunning && liveProgress ? liveProgress.overallPct : ((j as any).progress_pct ?? j.progressPct ?? 0);
                const collected = isRunning && liveProgress ? liveProgress.totalSamples : ((j as any).collected_samples ?? j.collectedSamples ?? 0);
                return (
                <tr key={j.id} style={isRunning ? { background: "rgba(59,130,246,0.04)" } : undefined}>
                  <td style={styles.td}><StatusBadge status={j.status} /></td>
                  <td style={styles.td}>{fmtDate((j as any).created_at ?? j.createdAt)}</td>
                  <td style={styles.td}>{(j as any).market_type ?? j.marketType ?? "-"}</td>
                  <td style={styles.td}>{((j as any).timeframes ?? j.timeframes)?.join(", ") ?? "-"}</td>
                  <td style={styles.td}>{(j as any).requested_samples ?? j.requestedSamples ?? "-"}</td>
                  <td style={styles.td}>
                    <span style={{ fontWeight: 600, color: isRunning ? "#3b82f6" : "#f1f5f9" }}>
                      {collected || "-"}
                    </span>
                  </td>
                  <td style={{ ...styles.td }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ flex: 1, height: "6px", borderRadius: "3px", background: "rgba(148,163,184,0.1)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min(100, progressPct)}%`, borderRadius: "3px", transition: "width 0.6s ease-out",
                          background: j.status === "completed" ? "#22c55e" : j.status === "failed" ? "#ef4444" : j.status === "cancelled" ? "#94a3b8" : "#3b82f6" }} />
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", minWidth: "32px" }}>
                        {progressPct.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td style={{ ...styles.td, color: "#ef4444", fontSize: "0.75rem" }}>{(j as any).error_message ?? j.errorMessage ?? "-"}</td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {(j.status === "pending" || j.status === "running") && (
                        <button
                          type="button"
                          onClick={async () => {
                            await xgbApi.cancelBackfillJob(j.id);
                            setJobs(prev => prev.map(x => x.id === j.id ? { ...x, status: "cancelled" as any } : x));
                            await refreshJobsSilently();
                          }}
                          style={{ padding: "4px 8px", fontSize: "0.7rem", borderRadius: "4px", background: "rgba(234,179,8,0.15)", color: "#eab308", border: "1px solid rgba(234,179,8,0.3)", cursor: "pointer" }}
                        >Cancel</button>
                      )}
                      {(j.status === "completed" || j.status === "failed" || j.status === "cancelled") && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm("Delete this backfill job?")) return;
                            await xgbApi.deleteBackfillJob(j.id);
                            setJobs(prev => prev.filter(x => x.id !== j.id));
                            await refreshJobsSilently();
                          }}
                          style={{ padding: "4px 8px", fontSize: "0.7rem", borderRadius: "4px", background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", cursor: "pointer" }}
                        >Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Performance Panel ── */
function PerformancePanel(): JSX.Element {
  const [passedFilter, setPassedFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("yesterday");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [limit, setLimit] = useState(20);
  const [symbol, setSymbol] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<xgbApi.PerformanceResponse | null>(null);
  const [generated, setGenerated] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const getDateRange = useCallback((): { from?: string; to?: string; hoursBack?: number } => {
    const now = new Date();
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    switch (datePreset) {
      case "today":
        return { from: fmt(now), to: fmt(now) };
      case "yesterday": {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        return { from: fmt(y), to: fmt(y) };
      }
      case "last3": {
        const d3 = new Date(now); d3.setDate(d3.getDate() - 2);
        return { from: fmt(d3), to: fmt(now) };
      }
      case "last7": {
        const d7 = new Date(now); d7.setDate(d7.getDate() - 6);
        return { from: fmt(d7), to: fmt(now) };
      }
      case "custom":
        return { from: customFrom, to: customTo };
      default:
        return { hoursBack: 48 };
    }
  }, [datePreset, customFrom, customTo]);

  const generateReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setGenerated(false);
    try {
      const range = getDateRange();
      let data: xgbApi.PerformanceResponse;
      if (range.from && range.to) {
        data = await xgbApi.getPerformance(48, passedFilter, directionFilter, limit, symbol || undefined, range.from, range.to, true);
      } else {
        data = await xgbApi.getPerformance(range.hoursBack ?? 48, passedFilter, directionFilter, limit, symbol || undefined, undefined, undefined, true);
      }
      setResult(data);
      setGenerated(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Performance analysis failed");
    } finally {
      setLoading(false);
    }
  }, [getDateRange, passedFilter, directionFilter, limit, symbol]);

  return (
    <div>
      <div style={{
        display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end",
        padding: "16px", borderRadius: "8px", background: "rgba(15,23,42,0.55)",
        border: "1px solid rgba(148,163,184,0.15)", marginBottom: "20px",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>Date</label>
          <select
            value={datePreset}
            onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); } }}
            style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem" }}
          >
            <option value="yesterday">Yesterday</option>
            <option value="today">Today (2h+ old only)</option>
            <option value="last3">Last 3 Days</option>
            <option value="last7">Last 7 Days</option>
            <option value="all">All (48h)</option>
            <option value="custom">Custom Date</option>
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>Status</label>
          <select
            value={passedFilter}
            onChange={e => setPassedFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem" }}
          >
            <option value="all">All Decisions</option>
            <option value="pass">Passed Only</option>
            <option value="fail">Rejected Only</option>
          </select>
        </div>
        {datePreset === "custom" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>From</label>
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>To</label>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem" }}
              />
            </div>
          </>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>Direction</label>
          <select
            value={directionFilter}
            onChange={e => setDirectionFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem" }}
          >
            <option value="all">All</option>
            <option value="up">UP Only</option>
            <option value="down">DOWN Only</option>
            <option value="sideways">SIDEWAYS Only</option>
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>Limit</label>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem" }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.7)", fontWeight: 600 }}>Symbol</label>
          <input
            type="text"
            placeholder="e.g. BTC/USDT"
            value={symbol}
            onChange={e => setSymbol(e.target.value.toUpperCase())}
            style={{ padding: "8px 12px", borderRadius: "6px", background: "#1e293b", border: "1px solid rgba(148,163,184,0.25)", color: "#f1f5f9", fontSize: "0.85rem", width: "120px" }}
          />
        </div>
        <Button
          type="button"
          onClick={generateReport}
          disabled={loading || (datePreset === "custom" && (!customFrom || !customTo))}
          style={{ padding: "8px 20px", fontSize: "0.85rem", height: "36px" }}
        >
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </div>

      {error && <ErrorState message={error} onRetry={generateReport} />}

      {loading && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "rgba(203,213,225,0.5)" }}>
          <div style={{ fontSize: "1rem", marginBottom: "8px" }}>Loading decisions and fetching prices from Bybit...</div>
          <div style={{ fontSize: "0.75rem", color: "rgba(148,163,184,0.5)" }}>May take a moment for larger result sets.</div>
        </div>
      )}

      {!loading && !error && !generated && (
        <EmptyState message="Select filters and click &quot;Generate Report&quot; to analyze XGBoost performance." />
      )}

      {!loading && generated && result && (
        <>
          <div style={styles.cardsRow}>
            <OverviewMetricCard label="Total Shown" value={String(result.total)} />
            <OverviewMetricCard label="Matched (unique symbols)" value={String(result.totalQueried)} />
            <OverviewMetricCard label="UP 1h" value={`${result.upCount} (${result.total > 0 ? ((result.upCount / result.total) * 100).toFixed(0) : 0}%)`} accent="#22c55e" />
            <OverviewMetricCard label="DOWN 1h" value={`${result.downCount} (${result.total > 0 ? ((result.downCount / result.total) * 100).toFixed(0) : 0}%)`} accent="#ef4444" />
            <OverviewMetricCard label="SIDEWAYS 1h" value={`${result.sidewaysCount} (${result.total > 0 ? ((result.sidewaysCount / result.total) * 100).toFixed(0) : 0}%)`} accent="#eab308" />
            <OverviewMetricCard label="Avg Change 1h" value={`${result.avgChange1h > 0 ? "+" : ""}${fmtNum(result.avgChange1h, 2)}%`} accent={result.avgChange1h >= 0 ? "#22c55e" : "#ef4444"} />
            <OverviewMetricCard label="Avg Change 2h" value={`${result.avgChange2h > 0 ? "+" : ""}${fmtNum(result.avgChange2h, 2)}%`} accent={result.avgChange2h >= 0 ? "#22c55e" : "#ef4444"} />
          </div>

          {result.items.length === 0 && (
            <EmptyState message="No decisions found matching the selected filters." />
          )}

          {/* ── Results Table ── */}
          {result.items.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Symbol</th>
                    <th style={styles.th}>XGB Score</th>
                    <th style={styles.th}>Threshold</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Model</th>
                    <th style={styles.th}>Price@Decision</th>
                    <th style={styles.th}>Price@+1h</th>
                    <th style={styles.th}>Chg 1h</th>
                    <th style={styles.th}>Dir 1h</th>
                    <th style={styles.th}>Price@+2h</th>
                    <th style={styles.th}>Chg 2h</th>
                    <th style={styles.th}>Dir 2h</th>
                    <th style={styles.th}>Lowest@2h</th>
                    <th style={styles.th}>Drop to Low</th>
                    <th style={styles.th}>Decision Time</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((item, idx) => {
                    const has1h = item.pctChange1h != null;
                    const has2h = item.pctChange2h != null;
                    const hasLow2h = item.lowestPrice2h != null && item.pctLowest2h != null;
                    const chg1Color = has1h ? (item.pctChange1h! > 0 ? "#22c55e" : item.pctChange1h! < 0 ? "#ef4444" : "#eab308") : "#6b7280";
                    const chg2Color = has2h ? (item.pctChange2h! > 0 ? "#22c55e" : item.pctChange2h! < 0 ? "#ef4444" : "#eab308") : "#6b7280";
                    const dir1Color = item.direction1h === "UP" ? "#22c55e" : item.direction1h === "DOWN" ? "#ef4444" : item.direction1h === "SIDEWAYS" ? "#eab308" : "#6b7280";
                    const dir2Color = item.direction2h === "UP" ? "#22c55e" : item.direction2h === "DOWN" ? "#ef4444" : item.direction2h === "SIDEWAYS" ? "#eab308" : "#6b7280";
                    return (
                      <tr key={`${item.symbol}-${idx}`}>
                        <td style={styles.td}>{idx + 1}</td>
                        <td style={{ ...styles.td, fontWeight: 600, color: "#f1f5f9" }}>{item.symbol}</td>
                        <td style={styles.td}>{fmtNum(item.score, 4)}</td>
                        <td style={styles.td}>{fmtNum(item.threshold, 4)}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.badge,
                            color: item.passed ? "#22c55e" : "#ef4444",
                            background: item.passed ? "rgba(34,197,94,0.13)" : "rgba(239,68,68,0.13)",
                          }}>{item.passed ? "PASS" : "REJECT"}</span>
                        </td>
                        <td style={{ ...styles.td, color: "rgba(203,213,225,0.6)", fontSize: "0.8rem" }}>{item.modelVersion ?? "-"}</td>
                        <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "0.82rem" }}>{item.priceAtDecision != null ? fmtNum(item.priceAtDecision, 6) : "N/A"}</td>
                        <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "0.82rem" }}>{item.priceAfter1h != null ? fmtNum(item.priceAfter1h, 6) : "N/A"}</td>
                        <td style={{ ...styles.td, color: chg1Color, fontWeight: 600 }}>
                          {has1h ? `${item.pctChange1h! > 0 ? "+" : ""}${fmtNum(item.pctChange1h!, 2)}%` : "N/A"}
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, color: dir1Color, background: `${dir1Color}22` }}>{item.direction1h ?? "N/A"}</span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "0.82rem" }}>{item.priceAfter2h != null ? fmtNum(item.priceAfter2h, 6) : "N/A"}</td>
                        <td style={{ ...styles.td, color: chg2Color, fontWeight: 600 }}>
                          {has2h ? `${item.pctChange2h! > 0 ? "+" : ""}${fmtNum(item.pctChange2h!, 2)}%` : "N/A"}
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, color: dir2Color, background: `${dir2Color}22` }}>{item.direction2h ?? "N/A"}</span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "0.82rem" }}>{item.lowestPrice2h != null ? fmtNum(item.lowestPrice2h, 6) : "N/A"}</td>
                        <td style={{ ...styles.td, color: "#ef4444", fontWeight: 600 }}>
                          {hasLow2h ? `${item.pctLowest2h! > 0 ? "+" : ""}${fmtNum(item.pctLowest2h!, 2)}%` : "N/A"}
                        </td>
                        <td style={{ ...styles.td, color: "rgba(203,213,225,0.7)", fontSize: "0.78rem" }}>{fmtDate(item.decisionTime)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: "8px 12px", borderRadius: "6px", background: "rgba(15,23,42,0.4)" }}>
      <div style={{ fontSize: "0.7rem", color: "rgba(203,213,225,0.5)", fontWeight: 600, textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "0.85rem", color: "#f1f5f9", wordBreak: "break-all" }}>{value}</div>
    </div>
  );
}

function OverviewMetricCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: accent ?? "#f1f5f9" }}>{value}</div>
    </div>
  );
}

function OverviewDetailPanel({ title, items }: { title: string; items: Array<{ label: string; value: string }> }) {
  return (
    <div style={{
      padding: "16px",
      borderRadius: "8px",
      background: "rgba(15, 23, 42, 0.55)",
      border: "1px solid rgba(148, 163, 184, 0.15)",
    }}>
      <h4 style={{ margin: "0 0 12px", color: "#f1f5f9", fontSize: "0.95rem" }}>{title}</h4>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
        {items.map((item) => (
          <div key={`${title}-${item.label}`} style={{ padding: "10px 12px", borderRadius: "6px", background: "rgba(30, 41, 59, 0.7)" }}>
            <div style={{ fontSize: "0.72rem", color: "rgba(203,213,225,0.55)", textTransform: "uppercase", marginBottom: "4px" }}>{item.label}</div>
            <div style={{ fontSize: "0.88rem", color: "#f1f5f9", wordBreak: "break-word" }}>{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewPanel({ overview, loading, error, onRetry }: { overview: XGBoostOverview | null; loading: boolean; error: string | null; onRetry: () => void }) {
  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!overview) return <EmptyState message="No data" />;
  return (
    <div>
      <div style={styles.cardsRow}>
        <OverviewMetricCard label="Enabled" value={overview.enabled ? "Yes" : "No"} accent={overview.enabled ? "#22c55e" : "#ef4444"} />
        <OverviewMetricCard label="Mode" value={overview.mode || "-"} />
        <OverviewMetricCard label="Threshold" value={fmtNum(overview.threshold, 2)} />
        <OverviewMetricCard label="Champion Model" value={overview.modelVersion ?? "-"} />
        <OverviewMetricCard label="Model Role" value={overview.modelRole ?? "-"} />
        <OverviewMetricCard label="24h Decisions" value={String(overview.decisions24h)} />
        <OverviewMetricCard label="Pass Rate 24h" value={overview.passRate24h != null ? `${overview.passRate24h}%` : "-"} accent={overview.passRate24h != null && overview.passRate24h >= 50 ? "#22c55e" : undefined} />
        <OverviewMetricCard label="Total Decisions" value={String(overview.totalDecisions)} />
        <OverviewMetricCard label="Total Samples" value={String(overview.totalSamples)} />
        <OverviewMetricCard label="Trainable Samples" value={String(overview.trainableSamples)} />
        <OverviewMetricCard label="Challenger Ready" value={overview.challengerAvailable ? "Yes" : "No"} accent={overview.challengerAvailable ? "#22c55e" : "#f59e0b"} />
        <OverviewMetricCard label="Gate Health" value={overview.gateHealthy ? "Healthy" : "Alert"} accent={overview.gateHealthy ? "#22c55e" : "#ef4444"} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "16px", marginTop: "16px" }}>
        <OverviewDetailPanel
          title="Model Lifecycle"
          items={[
            { label: "Trained At", value: overview.trainedAt ? fmtDate(overview.trainedAt) : "-" },
            { label: "Last Promotion", value: overview.lastPromotion ? fmtDate(overview.lastPromotion) : "-" },
            { label: "Last Retrain", value: overview.lastRetrain ? fmtDate(overview.lastRetrain) : "-" },
            { label: "Mode", value: overview.mode || "-" },
          ]}
        />
        <OverviewDetailPanel
          title="Gate Health"
          items={[
            { label: "Healthy", value: overview.gateHealthy ? "Yes" : "No" },
            { label: "Consecutive Timeouts", value: String(overview.gateConsecutiveTimeouts) },
            { label: "Total Fallbacks", value: String(overview.gateTotalFallbacks) },
            { label: "Alert Fired", value: overview.gateAlertFired ? "Yes" : "No" },
          ]}
        />
        <OverviewDetailPanel
          title="Decision Coverage"
          items={[
            { label: "24h Decisions", value: String(overview.decisions24h) },
            { label: "Pass Rate 24h", value: overview.passRate24h != null ? `${overview.passRate24h}%` : "-" },
            { label: "Total Decisions", value: String(overview.totalDecisions) },
            { label: "Trainable Samples", value: String(overview.trainableSamples) },
          ]}
        />
      </div>
    </div>
  );
}
